/**
 * Normalize a user-supplied link href to a resolvable, absolute URL.
 *
 * A rich-text editor lets people type a bare host (`example.com`) or an email
 * (`sam@acme.com`). Stored verbatim, a bare host becomes a *relative* href the
 * browser resolves against the current page — a dead link. This adds the
 * missing scheme so the stored string is correct everywhere it's consumed
 * (renderer, email, exports, plain-text previews), rather than patching it at
 * render time in one consumer.
 *
 * Rules, in order:
 *   - empty / whitespace           -> "" (caller treats as "no link")
 *   - already has a scheme         -> unchanged  (https:, mailto:, tel:, ...)
 *   - protocol-relative (//host)   -> unchanged  (inherits the page scheme)
 *   - in-app relative / anchor     -> unchanged  (starts with / # ?)
 *   - bare email (foo@bar.com)     -> mailto:foo@bar.com
 *   - otherwise                    -> https://<input>  (default to secure)
 *
 * Unsafe schemes (`javascript:`, `data:`, ...) are intentionally left for the
 * sanitizer's protocol allowlist to drop — this function only *adds* a missing
 * scheme, it never vouches for one that's already present.
 */
export function normalizeLinkHref(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";

  // A real URL scheme (http, mailto, tel, ...) never contains a dot; a host
  // with a port (`example.com:8080`) does, so the dot check keeps us from
  // mistaking a host:port for a scheme.
  const scheme = trimmed.match(/^([a-z][a-z0-9+.-]*):/i)?.[1];
  if (scheme && !scheme.includes(".")) return trimmed;

  // Protocol-relative URL — leave it to inherit the page's scheme.
  if (trimmed.startsWith("//")) return trimmed;

  // In-app relative path, anchor, or query — not an external target.
  if (/^[/#?]/.test(trimmed)) return trimmed;

  // Bare email address -> mailto:
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return `mailto:${trimmed}`;

  // A host or host/path missing its scheme -> default to https.
  return `https://${trimmed}`;
}
